import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../Providers/AuthContext';
import { submitFeedback } from '../../services/firebase/feedbackService';
import styles from './FeedbackModal.module.css';

const PAGE_OPTIONS = [
  'Main Menu',
  'Challenge AI',
  'Multiplayer',
  'Tutorial',
  'Sandbox',
  'Puzzles',
  'Other',
];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { user, userProfile } = useContext(AuthContext);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [page, setPage] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        resetAndClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPage('');
    setError('');
    setSuccess(false);
    setLoading(false);
  };

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!description.trim()) {
      setError('Please share your feedback');
      return;
    }

    setLoading(true);

    try {
      await submitFeedback({
        title: title.trim(),
        description: description.trim(),
        page: page || 'Not specified',
        reportedBy: user?.uid || 'anonymous',
        reportedByUsername: userProfile?.username || 'Anonymous',
        userAgent: navigator.userAgent,
        currentUrl: window.location.pathname,
      });
      setSuccess(true);
    } catch (err: any) {
      setError('Failed to send feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={resetAndClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={resetAndClose}>
          &times;
        </button>

        <h2 className={styles.title}>Send Feedback</h2>

        {success ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>&#10003;</div>
            <div className={styles.successText}>Feedback sent!</div>
            <div className={styles.successSubtext}>Thank you for helping shape Zodiac Chess.</div>
          </div>
        ) : (
          <>
            <p className={styles.description}>
              Got an idea or thought? We'd love to hear it.
            </p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Subject *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={styles.input}
                  placeholder="Brief summary"
                  maxLength={100}
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Your feedback *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={styles.textarea}
                  placeholder="Tell us what you think..."
                  maxLength={1000}
                  rows={4}
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>What page were you on?</label>
                <select
                  value={page}
                  onChange={(e) => setPage(e.target.value)}
                  className={styles.select}
                >
                  <option value="">Select a page...</option>
                  {PAGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {error && <div className={styles.error}>{error}</div>}

              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading || !title.trim() || !description.trim()}
              >
                {loading ? 'Sending...' : 'Send Feedback'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default FeedbackModal;
